import React, { useState, useRef, useEffect } from 'react';
import { API_URL } from '../../services/ApiService';

const MentionInput = ({ 
  value, 
  onChange, 
  mentions, 
  onMentionsChange, 
  placeholder = 'Write a comment...',
  disabled = false 
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Search for users when @ is typed
  useEffect(() => {
    const searchUsers = async () => {
      if (mentionSearch.length < 1) {
        setSuggestions([]);
        return;
      }

      console.log('MentionInput: Searching for users with query:', mentionSearch);
      try {
        const response = await fetch(
          `${API_URL}/users/search?q=${encodeURIComponent(mentionSearch)}&limit=10`,
          {
            credentials: 'include'
          }
        );
        
        console.log('MentionInput: Search response status:', response.status);
        if (response.ok) {
          const users = await response.json();
          console.log('MentionInput: Found users:', users);
          setSuggestions(users);
        } else {
          console.error('MentionInput: Search failed with status:', response.status);
        }
      } catch (err) {
        console.error('Error searching users:', err);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [mentionSearch]);

  // Handle textarea input
  const handleInput = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    console.log('MentionInput: Input detected, value:', newValue, 'cursor:', cursorPos);
    
    // Check if @ was typed (allow @ at start, after space, or after opening parenthesis)
    if (newValue[cursorPos - 1] === '@' && (cursorPos === 1 || [' ', '(', '\n', '\t'].includes(newValue[cursorPos - 2]))) {
      console.log('MentionInput: @ detected, starting mention mode');
      setMentionStartIndex(cursorPos - 1);
      setMentionSearch('');
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
    }
    // Update mention search if we're in mention mode
    else if (mentionStartIndex >= 0) {
      const searchText = newValue.substring(mentionStartIndex + 1, cursorPos);
      
      // Check if mention was cancelled (space, newline, or deleted @)
      if (searchText.includes(' ') || searchText.includes('\n') || !newValue.substring(mentionStartIndex).startsWith('@')) {
        setShowSuggestions(false);
        setMentionStartIndex(-1);
        setMentionSearch('');
      } else {
        setMentionSearch(searchText);
      }
    }
    
    onChange(newValue);
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
        
      case 'Enter':
      case 'Tab':
        if (showSuggestions && suggestions[selectedSuggestionIndex]) {
          e.preventDefault();
          insertMention(suggestions[selectedSuggestionIndex]);
        }
        break;
        
      case 'Escape':
        setShowSuggestions(false);
        setMentionStartIndex(-1);
        setMentionSearch('');
        break;
    }
  };

  // Insert selected mention
  const insertMention = (user) => {
    const beforeMention = value.substring(0, mentionStartIndex);
    const afterMention = value.substring(mentionStartIndex + mentionSearch.length + 1);
    const newValue = `${beforeMention}@${user.username} ${afterMention}`;
    
    onChange(newValue);
    
    // Add to mentions list if not already there
    if (!mentions.find(m => m.id === user._id)) {
      onMentionsChange([...mentions, { id: user._id, username: user.username }]);
    }
    
    // Reset mention state
    setShowSuggestions(false);
    setMentionStartIndex(-1);
    setMentionSearch('');
    setSelectedSuggestionIndex(0);
    
    // Focus back on textarea
    if (textareaRef.current) {
      const newCursorPos = beforeMention.length + user.username.length + 2;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }
  };

  // Get textarea position for suggestions dropdown
  const getSuggestionsPosition = () => {
    if (!textareaRef.current || mentionStartIndex < 0) return {};
    
    // Simple positioning - you may want to improve this
    return {
      position: 'absolute',
      bottom: '100%',
      left: '0',
      marginBottom: '4px'
    };
  };

  return (
    <div className="mention-input-container">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="mention-textarea"
        rows={3}
      />
      
      {/* Mention pills display */}
      {mentions.length > 0 && (
        <div className="mention-pills">
          {mentions.map(mention => (
            <span key={mention.id} className="mention-pill">
              @{mention.username}
              <button
                type="button"
                onClick={() => {
                  onMentionsChange(mentions.filter(m => m.id !== mention.id));
                }}
                className="remove-mention"
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="mention-suggestions"
          style={getSuggestionsPosition()}
        >
          {suggestions.map((user, index) => (
            <div
              key={user._id}
              className={`mention-suggestion ${index === selectedSuggestionIndex ? 'selected' : ''}`}
              onClick={() => insertMention(user)}
              onMouseEnter={() => setSelectedSuggestionIndex(index)}
            >
              {user.avatar && (
                <img src={user.avatar} alt={user.username} className="suggestion-avatar" />
              )}
              <span className="suggestion-username">@{user.username}</span>
              {user.name && (
                <span className="suggestion-name">{user.name}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;